import { trackEvent, trackEventV2 } from 'tracking/tracking';
import { TrackingPlaceInApp } from '@ardoq/data-model';
import { ViewIds } from '@ardoq/api-types';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import {
  DEFAULT_SOLUTION_METADATA,
  getSolutionMetadata,
  type SolutionMetadata,
} from '@ardoq/core';
import { loadedState$ } from 'loadedState/loadedState$';
import { traversals$ } from 'streams/traversals$';
import { isSingleTraversalState } from 'appLayout/ardoqStudio/saveAsViewpoint/saveAsViewpoint$/isSingleTraversalState';

const { VIEW_PANE_HEADER, VIEW_SETTINGS_PANEL } = TrackingPlaceInApp;

export const trackOpenViewSettingsPanel = () =>
  trackEvent(`${VIEW_PANE_HEADER} Opened view settings panel`);

export const trackViewStyleChange = (prevView: ViewIds, currentView: ViewIds) =>
  trackEvent(`${VIEW_SETTINGS_PANEL} Changed view style`, {
    viewStyle: viewIdToUserFriendlyName[currentView],
    previousViewStyle: viewIdToUserFriendlyName[prevView],
  });

export const trackViewOptionClick = (viewOption: string) =>
  trackEvent(`${VIEW_SETTINGS_PANEL} Clicked view option`, { viewOption });

const getCurrentViewpointSolutionMetadata = (): SolutionMetadata => {
  // eslint-disable-next-line ardoq-lint/no-direct-stream-state-access -- imperative one-shot save-as click handler; the analytics payload reads the loaded-viewpoint snapshot at click time
  const { loadedStates } = loadedState$.state;
  if (!isSingleTraversalState(loadedStates)) {
    return DEFAULT_SOLUTION_METADATA;
  }
  const { traversalId } = loadedStates[0];
  if (!traversalId) {
    return DEFAULT_SOLUTION_METADATA;
  }
  // eslint-disable-next-line ardoq-lint/no-direct-stream-state-access -- imperative one-shot save-as click handler; the analytics payload reads the loaded-viewpoint snapshot at click time
  const traversal = traversals$.state.byId[traversalId];
  return traversal ? getSolutionMetadata(traversal) : DEFAULT_SOLUTION_METADATA;
};

type SavedAs =
  | 'viewpoint'
  | 'slide'
  | 'png'
  | 'svg'
  | 'dashboard'
  | 'plantuml'
  | 'pptx';
export const trackClickOnSaveAs = (savedAs: SavedAs) =>
  trackEvent(`${VIEW_PANE_HEADER} Clicked saved as`, {
    savedAs,
    ...getCurrentViewpointSolutionMetadata(),
  });

export const trackViewOptionsApply = (viewId: ViewIds) =>
  trackEvent(`${VIEW_SETTINGS_PANEL} Applied view options`, {
    activeView: viewIdToUserFriendlyName[viewId],
  });

type TrackViewSettingsApplyMetadata = {
  referenceLabelsCount: number;
  componentLabelsCount: number;
  viewStyle: ViewIds;
};

export const trackMultiLabelFormattingApply = ({
  referenceLabelsCount,
  componentLabelsCount,
  viewStyle,
}: TrackViewSettingsApplyMetadata) =>
  trackEvent(`${VIEW_SETTINGS_PANEL} Applied multi-label formatting`, {
    referenceLabelsCount,
    componentLabelsCount,
    viewStyle,
  });

type TrackConditionalFormattingApplyMetadata = {
  componentRulesCount: number;
  referenceRulesCount: number;
  tagRulesCount: number;
  keepOriginalColor: boolean;
  activeViewId: ViewIds;
};

export const trackConditionalFormattingApply = (
  metadata: TrackConditionalFormattingApplyMetadata
) =>
  trackEventV2({
    event: `${VIEW_SETTINGS_PANEL} Applied conditional formatting`,
    payload: {
      componentRulesCount: metadata.componentRulesCount,
      referenceRulesCount: metadata.referenceRulesCount,
      tagRulesCount: metadata.tagRulesCount,
      keepOriginalColor: metadata.keepOriginalColor,
      activeView: viewIdToUserFriendlyName[metadata.activeViewId],
    },
  });

export const trackConditionalFormattingClear = (activeViewId: ViewIds) =>
  trackEventV2({
    event: `${VIEW_SETTINGS_PANEL} Cleared conditional formatting`,
    payload: {
      activeView: viewIdToUserFriendlyName[activeViewId],
    },
  });

export const trackMultiLabelFormattingClear = (activeViewId: ViewIds) =>
  trackEventV2({
    event: `${VIEW_SETTINGS_PANEL} Cleared multi-label formatting`,
    payload: {
      activeView: viewIdToUserFriendlyName[activeViewId],
    },
  });

export const trackViewOptionsClose = (activeViewId: ViewIds) =>
  trackEventV2({
    event: `${VIEW_SETTINGS_PANEL} Closed view options`,
    payload: {
      activeView: viewIdToUserFriendlyName[activeViewId],
    },
  });

export const trackOptimizeLayoutClick = (activeViewId: ViewIds) =>
  trackEventV2({
    event: `${VIEW_PANE_HEADER} Clicked optimize layout`,
    payload: {
      activeView: viewIdToUserFriendlyName[activeViewId],
    },
  });
